import React from 'react'
import {
  Plus, Filter, Edit, Eye, Target, TrendingUp, Users,
  X, MoreVertical, Trash2, Book
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ViewMode } from '@/utils/templateImportUtils'

interface ObjectivesTabContentProps {
  viewMode: ViewMode
  loading: boolean
  objectives: any[]
  filteredObjectives: any[]
  handleCreateObjective: () => void
  handleViewObjective: (objective: any) => void
  handleEditObjective: (objective: any) => void
  handleDeleteObjective: (objectiveId: number) => void
  handleToggleLibraryObjective: (objectiveId: number, currentStatus: boolean) => void
  clearFilters: () => void
}

const ObjectivesTabContent: React.FC<ObjectivesTabContentProps> = ({
  viewMode,
  loading,
  objectives,
  filteredObjectives,
  handleCreateObjective,
  handleViewObjective,
  handleEditObjective,
  handleDeleteObjective,
  handleToggleLibraryObjective,
  clearFilters,
}) => {
  const renderObjectiveCard = (objective: any, index: number) => {
    const objId = objective.id
    const objStatus = objective.is_library_objective
    return (
      <Card key={`obj-${objId}-${index}`} className="hover:shadow-md transition-shadow">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900">{objective.title}</h4>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{objective.description}</p>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {objective.kpa && (
                      <Badge variant="outline">
                        <Target className="h-3 w-3 mr-1" />
                        {objective.kpa.display_name}
                      </Badge>
                    )}
                    {objective.pillar && (
                      <Badge variant="outline" style={{ backgroundColor: `${objective.pillar.color_code}20`, color: objective.pillar.color_code }}>
                        {objective.pillar.name}
                      </Badge>
                    )}
                    {objective.role_type && (
                      <Badge variant="outline" className="bg-blue-50">
                        {objective.role_type}
                      </Badge>
                    )}
                    {objective.status && (
                      <Badge variant="outline" className={
                        objective.status === 'active' ? 'bg-green-50 text-green-700' :
                        objective.status === 'completed' ? 'bg-slate-50 text-slate-700' :
                        'bg-amber-50 text-amber-700'
                      }>
                        {objective.status}
                      </Badge>
                    )}
                    {objective.kpi_count > 0 && (
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        {objective.kpi_count} KPI{objective.kpi_count > 1 ? 's' : ''}
                      </Badge>
                    )}
                    {objective.usage_stats && objective.usage_stats.total_instances > 0 && (
                      <Badge variant="outline" className="bg-purple-50 text-purple-700">
                        <Users className="h-3 w-3 mr-1" />
                        {objective.usage_stats.total_instances} use{objective.usage_stats.total_instances > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleToggleLibraryObjective(objId, objStatus)
                }}
                className="flex items-center gap-2 text-sm font-medium cursor-pointer hover:bg-slate-50 px-3 py-2 rounded-md transition-colors"
              >
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                  objStatus
                    ? 'bg-heritage-blue border-heritage-blue'
                    : 'border-slate-300 bg-white'
                }`}>
                  {objStatus && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <Book className="h-4 w-4" />
                <span>{objStatus ? 'In Library' : 'Add to Library'}</span>
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleViewObjective(objective)}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleEditObjective(objective)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteObjective(objective.id)}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Institutional Objectives Library</h3>
          <p className="text-sm text-slate-600 mt-1">
            Manage individual objectives (Level 4) with KPIs and KPA linkage. Mark objectives for institutional library to make them available in templates.
          </p>
        </div>
        <Button onClick={handleCreateObjective} className="bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4 mr-2" />
          Create Objective
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16 bg-slate-50 rounded-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-slate-600 mt-4 font-medium">Loading objectives...</p>
        </div>
      ) : objectives.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-lg">
          <Target className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No objectives yet</h3>
          <p className="text-slate-600 mb-6">
            Create strategic objectives that can be used in templates
          </p>
          <Button onClick={handleCreateObjective} className="bg-green-600 hover:bg-green-700">
            <Plus className="h-4 w-4 mr-2" />
            Create First Objective
          </Button>
        </div>
      ) : filteredObjectives.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-lg">
          <Filter className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No objectives match your filters</h3>
          <p className="text-slate-600 mb-6">
            Try adjusting your filters or search terms
          </p>
          <Button onClick={clearFilters} variant="outline">
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredObjectives.map((objective: any, index: number) => renderObjectiveCard(objective, index))}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="space-y-3">
              {filteredObjectives.map((objective: any, index: number) => renderObjectiveCard(objective, index))}
            </div>
          )}

          {/* Compact View */}
          {viewMode === 'compact' && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 grid grid-cols-12 gap-4 text-xs font-semibold text-slate-600 uppercase">
                <div className="col-span-3">Title</div>
                <div className="col-span-2">KPA</div>
                <div className="col-span-2">Pillar</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-1">KPIs</div>
                <div className="col-span-1">Uses</div>
                <div className="col-span-1">Library</div>
                <div className="col-span-1">Actions</div>
              </div>
              <div className="divide-y divide-slate-200">
                {filteredObjectives.map((objective: any, index: number) => {
                  const objId = objective.id
                  const objStatus = objective.is_library_objective
                  return (
                    <div key={`obj-${objId}-${index}`} className="px-4 py-3 grid grid-cols-12 gap-4 items-center hover:bg-slate-50 transition-colors">
                      <div className="col-span-3">
                        <p className="text-sm font-medium text-slate-900 line-clamp-1">{objective.title}</p>
                        <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{objective.description}</p>
                      </div>
                      <div className="col-span-2">
                        {objective.kpa ? (
                          <Badge variant="outline" className="text-xs">
                            {objective.kpa.display_name}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </div>
                      <div className="col-span-2">
                        {objective.pillar ? (
                          <Badge variant="outline" className="text-xs" style={{ backgroundColor: `${objective.pillar.color_code}20`, color: objective.pillar.color_code }}>
                            {objective.pillar.name}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </div>
                      <div className="col-span-1">
                        <Badge variant="outline" className={`text-xs ${
                          objective.status === 'active' ? 'bg-green-50 text-green-700' :
                          objective.status === 'completed' ? 'bg-slate-50 text-slate-700' :
                          'bg-amber-50 text-amber-700'
                        }`}>
                          {objective.status}
                        </Badge>
                      </div>
                      <div className="col-span-1 text-sm text-slate-600">
                        {objective.kpi_count || 0}
                      </div>
                      <div className="col-span-1 text-sm text-slate-600">
                        {objective.usage_stats?.total_instances || 0}
                      </div>
                      <div className="col-span-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleToggleLibraryObjective(objId, objStatus)
                          }}
                          className="inline-flex items-center gap-1 text-xs"
                        >
                          <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${
                            objStatus
                              ? 'bg-heritage-blue border-heritage-blue'
                              : 'border-slate-300 bg-white'
                          }`}>
                            {objStatus && (
                              <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </button>
                      </div>
                      <div className="col-span-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <MoreVertical className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewObjective(objective)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditObjective(objective)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteObjective(objective.id)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ObjectivesTabContent
